import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';

type OnHoldRow = Database['public']['Tables']['on_holds']['Row'];
type OnHoldInsert = Database['public']['Tables']['on_holds']['Insert'];
type OnHoldUpdate = Database['public']['Tables']['on_holds']['Update'];
type ProductRow = Database['public']['Tables']['products']['Row'];
type CustomerRow = Database['public']['Tables']['customers']['Row'];

export interface OnHoldItemWithDetails extends OnHoldRow {
  product: ProductRow | null;
  customer: CustomerRow | null;
  linked_sale?: {
    id: string;
    sale_items?: Array<{
      id: string;
      sale_price_each: number;
      quantity: number;
      line_total_net: number;
    }>;
  } | null;
}

export interface OnHoldFilters {
  searchTerm?: string;
  dateFilter?: 'all' | 'today' | 'week' | 'month' | 'old';
  companyId: string;
  /** When set, only on-holds for this location (same as Reports). */
  locationId?: string | null;
}

export function useOnHold() {
  const [onHoldItems, setOnHoldItems] = useState<OnHoldItemWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentLocation } = useLocation();

  const fetchOnHoldItems = useCallback(async (filters: OnHoldFilters) => {
    setLoading(true);
    try {
      let query = supabase
        .from('on_holds')
        .select(`
          *,
          product:products(*),
          customer:customers(*)
        `)
        .eq('company_id', filters.companyId)
        .eq('status', 'on_hold')
        .order('date_on_hold', { ascending: false });

      if (filters.locationId) {
        query = query.eq('location_id', filters.locationId);
      }

      // Apply date filter
      if (filters.dateFilter && filters.dateFilter !== 'all') {
        const now = new Date();
        let startDate: string;
        
        switch (filters.dateFilter) {
          case 'today':
            startDate = now.toISOString().split('T')[0];
            query = query.gte('date_on_hold', startDate);
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            query = query.gte('date_on_hold', startDate);
            break;
          case 'month':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            query = query.gte('date_on_hold', startDate);
            break;
          case 'old':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            query = query.lt('date_on_hold', startDate);
            break;
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching on-hold items:', error);
        toast({
          title: "Hata",
          description: "Beklemedeki ürünler yüklenemedi",
          variant: "destructive",
        });
        return;
      }

      let filteredData = data || [];

      // Fetch linked sale items for on-hold records that have linked_sale_id
      const linkedSaleIds = filteredData
        .map((item: any) => item.linked_sale_id)
        .filter((id: string | null) => id !== null) as string[];
      
      let linkedSaleItemsMap = new Map<string, any>();
      if (linkedSaleIds.length > 0) {
        const { data: linkedSalesData } = await supabase
          .from('sale_items')
          .select('sale_id, sale_price_each, quantity, line_total_net, product_id')
          .in('sale_id', linkedSaleIds);
        
        if (linkedSalesData) {
          // Group by sale_id and product_id to match with on-hold items
          linkedSalesData.forEach((item: any) => {
            const key = `${item.sale_id}_${item.product_id}`;
            if (!linkedSaleItemsMap.has(key)) {
              linkedSaleItemsMap.set(key, item);
            }
          });
        }
      }

      // Enrich filteredData with linked sale item prices
      const enrichedData = filteredData.map((item: any) => {
        // If there's a linked sale, try to get the price from sale_items
        if (item.linked_sale_id && item.product_id) {
          const saleItemKey = `${item.linked_sale_id}_${item.product_id}`;
          const linkedSaleItem = linkedSaleItemsMap.get(saleItemKey);
          
          if (linkedSaleItem && linkedSaleItem.sale_price_each) {
            // Create a new object with the effective price from linked sale
            return {
              ...item,
              effective_price: linkedSaleItem.sale_price_each,
              effective_quantity: linkedSaleItem.quantity || item.hold_quantity,
            };
          }
        }
        
        // Fallback to hold_price
        return {
          ...item,
          effective_price: item.hold_price || 0,
          effective_quantity: item.hold_quantity || 0,
        };
      });

      // Apply search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const searchFiltered = enrichedData.filter((item: any) => 
          item.product?.name?.toLowerCase().includes(searchLower) ||
          item.product?.item_code?.toLowerCase().includes(searchLower) ||
          item.customer?.first_name?.toLowerCase().includes(searchLower) ||
          item.customer?.last_name?.toLowerCase().includes(searchLower) ||
          item.customer?.phone?.includes(filters.searchTerm)
        );
        setOnHoldItems(searchFiltered);
      } else {
        setOnHoldItems(enrichedData);
      }
    } catch (error) {
      console.error('Error in fetchOnHoldItems:', error);
      toast({
        title: "Hata",
        description: "Beklemedeki ürünler yüklenemedi",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const convertToSale = useCallback(async (onHoldId: string) => {
    try {
      setLoading(true);

      // First, get the on-hold item details
      const { data: onHoldItem, error: fetchError } = await supabase
        .from('on_holds')
        .select(`
          *,
          product:products(*),
          customer:customers(*)
        `)
        .eq('id', onHoldId)
        .single();

      if (fetchError || !onHoldItem) {
        throw new Error('On-hold item not found');
      }

      const resolvedLocationId = onHoldItem.location_id ?? currentLocation?.id ?? null;
      if (!resolvedLocationId) {
        throw new Error('Location is required to convert on-hold item to sale');
      }

      // Check if product has enough stock
      if (!onHoldItem.product || onHoldItem.product.quantity_on_hand < onHoldItem.hold_quantity) {
        toast({
          title: "Yetersiz Stok",
          description: "Ürün için yeterli stok bulunmuyor",
          variant: "destructive",
        });
        return false;
      }

      // Start a transaction to:
      // 1. Update on-hold status to 'converted'
      // 2. Create a sale
      // 3. Create sale items
      // 4. Update inventory

      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          customer_id: onHoldItem.customer_id,
          status: 'open',
          total_gross: (onHoldItem.hold_price * onHoldItem.hold_quantity),
          total_discount: 0,
          total_net: (onHoldItem.hold_price * onHoldItem.hold_quantity),
          total_paid: 0,
          balance_due: (onHoldItem.hold_price * onHoldItem.hold_quantity),
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (saleError || !sale) {
        throw new Error('Failed to create sale');
      }

      // Create sale item
      const { error: saleItemError } = await supabase
        .from('sale_items')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          sale_id: sale.id,
          product_id: onHoldItem.product_id,
          quantity: onHoldItem.hold_quantity,
          sale_price_each: onHoldItem.hold_price,
          cost_each: onHoldItem.hold_cost,
          discount: 0,
          line_total_gross: (onHoldItem.hold_price * onHoldItem.hold_quantity),
          line_total_net: (onHoldItem.hold_price * onHoldItem.hold_quantity),
        });

      if (saleItemError) {
        throw new Error('Failed to create sale item');
      }

      // Update on-hold status and link to sale
      const { error: updateError } = await supabase
        .from('on_holds')
        .update({
          status: 'converted',
          linked_sale_id: sale.id,
        })
        .eq('id', onHoldId);

      if (updateError) {
        throw new Error('Failed to update on-hold status');
      }

      // Update product inventory
      const { error: inventoryError } = await supabase
        .from('products')
        .update({
          quantity_on_hand: onHoldItem.product.quantity_on_hand - onHoldItem.hold_quantity,
        })
        .eq('id', onHoldItem.product_id);

      if (inventoryError) {
        throw new Error('Failed to update inventory');
      }

      // Create inventory movement record
      await supabase
        .from('inventory_movements')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          product_id: onHoldItem.product_id,
          movement_type: 'sale_out',
          qty: onHoldItem.hold_quantity,
          related_table: 'sales',
          related_id: sale.id,
          actor_user_id: user?.id || null,
        });

      // Create calendar reminder for customer satisfaction call (4 days after sale date)
      // Use the sale's sale_datetime or created_at as the base date
      const saleDate = sale.sale_datetime ? new Date(sale.sale_datetime) : new Date(sale.created_at);
      const reminderDate = new Date(saleDate);
      reminderDate.setDate(reminderDate.getDate() + 4);
      
      const customerName = onHoldItem.customer ? 
        `${onHoldItem.customer.first_name || ''} ${onHoldItem.customer.last_name || ''}`.trim() || 'Müşteri' :
        'Müşteri';
      
      const { error: calendarError } = await supabase
        .from('calendar_events')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          title: 'Memnuniyet Araması',
          description: `${customerName} müşteriyi arayıp sürecin nasıl geçtiğini sorun, memnuniyet ölçün`,
          start_date: reminderDate.toISOString().split('T')[0],
          start_time: '10:00:00',
          event_type: 'follow_up',
          category: 'sales',
          priority: 'medium',
          status: 'scheduled',
          customer_id: onHoldItem.customer_id,
          sale_id: sale.id,
          reminder_minutes_before: [60, 1440], // 1 hour and 1 day before
          metadata: {
            purpose: 'customer_satisfaction',
            sale_amount: onHoldItem.hold_price * onHoldItem.hold_quantity,
            product_count: 1,
            converted_from_onhold: true
          }
        });

      if (calendarError) {
        console.warn('Calendar reminder creation failed:', calendarError);
        // Don't throw error here - sale should still succeed even if reminder fails
      }

      toast({
        title: "Satışa Dönüştürüldü",
        description: `${onHoldItem.product.name} başarıyla satışa dönüştürüldü`,
      });

      return true;
    } catch (error) {
      console.error('Error converting to sale:', error);
      toast({
        title: "Hata",
        description: "Satışa dönüştürme işlemi başarısız",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, user?.id, currentLocation?.id]);

  const replaceProduct = useCallback(async (onHoldId: string, newProductId: string) => {
    try {
      setLoading(true);

      // Get the new product details
      const { data: newProduct, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', newProductId)
        .single();

      if (productError || !newProduct) {
        throw new Error('New product not found');
      }

      // Get the on-hold item to check quantity
      const { data: onHoldItem, error: onHoldError } = await supabase
        .from('on_holds')
        .select('hold_quantity')
        .eq('id', onHoldId)
        .single();

      if (onHoldError || !onHoldItem) {
        throw new Error('On-hold item not found');
      }

      // Check if new product has enough stock
      if (newProduct.quantity_on_hand < onHoldItem.hold_quantity) {
        toast({
          title: "Yetersiz Stok",
          description: `${newProduct.name} için yeterli stok bulunmuyor`,
          variant: "destructive",
        });
        return false;
      }

      // Update the on-hold item with new product
      const { error: updateError } = await supabase
        .from('on_holds')
        .update({
          product_id: newProductId,
        })
        .eq('id', onHoldId);

      if (updateError) {
        throw new Error('Failed to update on-hold item');
      }

      toast({
        title: "Ürün Değiştirildi",
        description: `Ürün ${newProduct.name} ile değiştirildi`,
      });

      return true;
    } catch (error) {
      console.error('Error replacing product:', error);
      toast({
        title: "Hata",
        description: "Ürün değiştirme işlemi başarısız",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const replaceAndSendProduct = useCallback(async (onHoldId: string, newProductId: string) => {
    try {
      setLoading(true);

      // Get the on-hold item details
      const { data: onHoldItem, error: fetchError } = await supabase
        .from('on_holds')
        .select(`
          *,
          product:products(*),
          customer:customers(*)
        `)
        .eq('id', onHoldId)
        .single();

      if (fetchError || !onHoldItem) {
        throw new Error('On-hold item not found');
      }

      const resolvedLocationId = onHoldItem.location_id ?? currentLocation?.id ?? null;
      if (!resolvedLocationId) {
        throw new Error('Location is required to convert on-hold item to sale');
      }

      // Get the new product details
      const { data: newProduct, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', newProductId)
        .single();

      if (productError || !newProduct) {
        throw new Error('New product not found');
      }

      // Check if new product has enough stock
      if (newProduct.quantity_on_hand < onHoldItem.hold_quantity) {
        toast({
          title: "Yetersiz Stok",
          description: `${newProduct.name} için yeterli stok bulunmuyor`,
          variant: "destructive",
        });
        return false;
      }

      // Create a sale with the replacement product
      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          customer_id: onHoldItem.customer_id,
          status: 'open',
          total_gross: (newProduct.price * onHoldItem.hold_quantity),
          total_discount: 0,
          total_net: (newProduct.price * onHoldItem.hold_quantity),
          total_paid: 0,
          balance_due: (newProduct.price * onHoldItem.hold_quantity),
        })
        .select()
        .single();

      if (saleError || !sale) {
        throw new Error('Failed to create sale');
      }

      // Create sale item with replacement product
      const { error: saleItemError } = await supabase
        .from('sale_items')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          sale_id: sale.id,
          product_id: newProductId,
          quantity: onHoldItem.hold_quantity,
          sale_price_each: newProduct.price,
          cost_each: newProduct.current_cost || newProduct.initial_cost,
          discount: 0,
          line_total_gross: (newProduct.price * onHoldItem.hold_quantity),
          line_total_net: (newProduct.price * onHoldItem.hold_quantity),
        });

      if (saleItemError) {
        throw new Error('Failed to create sale item');
      }

      // Update on-hold status and link to sale
      const { error: updateError } = await supabase
        .from('on_holds')
        .update({
          status: 'converted',
          linked_sale_id: sale.id,
          product_id: newProductId, // Update to replacement product
        })
        .eq('id', onHoldId);

      if (updateError) {
        throw new Error('Failed to update on-hold status');
      }

      // Update replacement product inventory
      const { error: inventoryError } = await supabase
        .from('products')
        .update({
          quantity_on_hand: newProduct.quantity_on_hand - onHoldItem.hold_quantity,
        })
        .eq('id', newProductId);

      if (inventoryError) {
        throw new Error('Failed to update inventory');
      }

      // Create inventory movement record for replacement product
      await supabase
        .from('inventory_movements')
        .insert({
          company_id: onHoldItem.company_id,
          location_id: resolvedLocationId,
          product_id: newProductId,
          movement_type: 'sale_out',
          qty: onHoldItem.hold_quantity,
          related_table: 'sales',
          related_id: sale.id,
          actor_user_id: user?.id || null,
        });

      toast({
        title: "Muadil Ürün Gönderildi",
        description: `${newProduct.name} ile satış tamamlandı`,
      });

      return true;
    } catch (error) {
      console.error('Error replacing and sending product:', error);
      toast({
        title: "Hata",
        description: "Muadil ürün gönderme işlemi başarısız",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, user?.id, currentLocation?.id]);

  const deleteOnHoldItem = useCallback(async (onHoldId: string) => {
    try {
      setLoading(true);

      // Update status to 'returned' instead of deleting
      const { error } = await supabase
        .from('on_holds')
        .update({ status: 'returned' })
        .eq('id', onHoldId);

      if (error) {
        throw new Error('Failed to remove on-hold item');
      }

      toast({
        title: "Silindi",
        description: "Beklemedeki ürün başarıyla silindi",
      });

      return true;
    } catch (error) {
      console.error('Error deleting on-hold item:', error);
      toast({
        title: "Hata",
        description: "Silme işlemi başarısız",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const createOnHoldItem = useCallback(async (data: Omit<OnHoldInsert, 'id' | 'created_at' | 'hold_price' | 'hold_cost'> & { product_price: number; product_cost: number }) => {
    try {
      setLoading(true);

      const { data: newItem, error } = await supabase
        .from('on_holds')
        .insert({
          company_id: data.company_id,
          location_id: data.location_id ?? currentLocation?.id ?? null,
          customer_id: data.customer_id,
          product_id: data.product_id,
          hold_quantity: data.hold_quantity,
          hold_price: data.product_price,
          hold_cost: data.product_cost,
          status: 'on_hold',
          date_on_hold: new Date().toISOString().split('T')[0],
        })
        .select(`
          *,
          product:products(*),
          customer:customers(*)
        `)
        .single();

      if (error) {
        throw new Error('Failed to create on-hold item');
      }

      toast({
        title: "Beklemeye Alındı",
        description: "Ürün başarıyla beklemeye alındı",
      });

      return newItem;
    } catch (error) {
      console.error('Error creating on-hold item:', error);
      toast({
        title: "Hata",
        description: "Beklemeye alma işlemi başarısız",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, currentLocation?.id]);

  return {
    onHoldItems,
    loading,
    fetchOnHoldItems,
    convertToSale,
    replaceProduct,
    replaceAndSendProduct,
    deleteOnHoldItem,
    createOnHoldItem,
  };
}
